import _ from 'lodash'
import { useCallback, useMemo, useRef } from 'react'
import { Location, useLocation, useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import { t } from '@lingui/macro'
import * as yup from 'yup'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'
import AutocompleteField from '@/components/formik/fields/AutocompleteField'
import { SelectOption } from '@/components/ui/inputs/SelectInput'
import SelectField from '@/components/formik/fields/SelectField'
import TextField from '@/components/formik/fields/TextField'
import { Skeleton } from '@mui/material'

export type ReserveConsultationFormValues = {
  patient: string
  reason?: string
  speciality: string
}

export type ReserveConsultationDialogProps = {
  canReserve: boolean
  isLoading?: boolean
  isReserving?: boolean
  onSearchPatient?: (value: string) => void
  onReserveConsultation: (values: ReserveConsultationFormValues) => void
  patientOptions?: AutocompleteOption[]
  reasonOptions?: string[]
  specialityOptions?: SelectOption[]
  isPaid: boolean
}

export function ReserveConsultationDialog(props: ReserveConsultationDialogProps) {
  const {
    canReserve,
    isLoading = false,
    isReserving = false,
    onSearchPatient,
    onReserveConsultation,
    patientOptions = [],
    reasonOptions = [],
    specialityOptions = [],
    isPaid,
  } = props
  const location = useLocation()
  const navigate = useNavigate()

  const background: Location | undefined = _.get(location.state, 'background')

  const reasonInputContainer = useRef<HTMLDivElement>(null)

  const initialValues = useMemo(
    () => ({
      patient: '',
      reason: '',
      speciality: '',
    }),
    []
  )

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        patient: yup.string().required(),
        speciality: yup.string().required(),
      }),
    []
  )

  const handleClose = () => {
    navigate(background ? background.pathname : '/')
  }

  const handleSearchPatient = useCallback(
    (inputValue: string) => {
      onSearchPatient?.(inputValue)
    },
    [onSearchPatient]
  )

  const handleSubmit = (values: ReserveConsultationFormValues) => {
    onReserveConsultation(values)
  }

  return (
    <Dialog
      disablePortal
      open
      onClose={(_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason !== 'backdropClick') handleClose()
      }}
      PaperProps={{ sx: { maxWidth: (theme) => theme.breakpoints.values.sm, width: '100%', overflow: 'visible' } }}
    >
      {isLoading ? (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
            {t`Reserve a consultation`}
          </DialogTitle>
          <DialogContent>
            <Skeleton variant="rounded" width="100%" height={60} sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton variant="text" width={150} sx={{ fontSize: '0.875rem' }} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="text" width={150} sx={{ fontSize: '0.875rem' }} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="text" width={150} sx={{ fontSize: '0.875rem' }} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </Grid>
            </Grid>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
            {t`Reserve a consultation`}
            <Tooltip title={t`Close`}>
              <IconButton onClick={handleClose}>
                <CloseRoundedIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent>
            {!canReserve && (
              <Alert icon={false} severity="warning">
                {t`You cannot reserve a consultation yet.`}
              </Alert>
            )}
            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
              {(formikProps) => (
                <Form>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <AutocompleteField
                        freeSolo={false}
                        label={t`Patient`}
                        name="patient"
                        onInputChange={handleSearchPatient}
                        options={patientOptions}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectField fullWidth label={t`Speciality`} name="speciality" options={specialityOptions} />
                    </Grid>
                    <Grid item xs={12}>
                      <Box ref={reasonInputContainer}>
                        <TextField
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <PopupState variant="popover" popupId="reason-options">
                                {(popupState) => (
                                  <>
                                    <IconButton size="small" {...bindTrigger(popupState)}>
                                      <KeyboardArrowDownIcon />
                                    </IconButton>
                                    <Menu
                                      {...bindMenu(popupState)}
                                      disablePortal
                                      anchorReference="anchorPosition"
                                      anchorPosition={{
                                        top: reasonInputContainer.current?.getBoundingClientRect().bottom || 0,
                                        left: reasonInputContainer.current?.getBoundingClientRect().left || 0,
                                      }}
                                      PaperProps={{
                                        sx: [
                                          reasonInputContainer.current && {
                                            minWidth: reasonInputContainer.current.getBoundingClientRect().width,
                                          },
                                        ],
                                      }}
                                    >
                                      {_.map(reasonOptions, (option, index) => (
                                        <MenuItem
                                          key={`${option}-${index}`}
                                          onClick={() => {
                                            formikProps.setFieldValue('reason', option)
                                            popupState.close()
                                          }}
                                        >
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Menu>
                                  </>
                                )}
                              </PopupState>
                            ),
                          }}
                          label={t`Consultation reason`}
                          name="reason"
                        />
                      </Box>
                    </Grid>
                    {isPaid && (
                      <Grid item xs={12}>
                        <Alert severity="info">{t`Payment is enabled for consultations`}</Alert>
                      </Grid>
                    )}
                  </Grid>
                  <Stack direction="row" justifyContent="end" mt={4} spacing={1}>
                    <Button onClick={handleClose} variant="neutral">
                      {t`Cancel`}
                    </Button>
                    <LoadingButton disabled={!canReserve} loading={isReserving} onClick={formikProps.submitForm} type="button" variant="contained" data-testid="reserve-consultation-submit">
                      {t`Submit`}
                    </LoadingButton>
                  </Stack>
                </Form>
              )}
            </Formik>
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

export default ReserveConsultationDialog
